import * as React from 'react';
import {
  Button, Card, CardActions, CardHeader, CardContent, CircularProgress,
  FormControl, Grid, InputLabel, TextField, MenuItem, Select, Stack,
  Typography, IconButton, InputAdornment, Divider, Chip
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useState, useEffect } from 'react';
import MonthSelector from '../common/MonthSelector';
import useEnvConfig from '../util/useEnvConfig';

const DownloadReportsPanel = ({ accessToken, onRequestSubmitted, appConfig }) => {
  const { rootRestApiEndPoint } = useEnvConfig();
  const [loading, setLoading] = React.useState(true);
  // Plant (legacy)
  const [plants, setPlants] = React.useState([]);
  const [selectedPlantId, setSelectedPlantId] = React.useState('');
  // PT site search
  const [guess, setGuess] = React.useState('');
  const [euGuess, setEuGuess] = React.useState('');
  const [sites, setSites] = React.useState(null);
  const [selectedSiteId, setSelectedSiteId] = React.useState('');
  // Portfolio groups
  const [portfolioGroups, setPortfolioGroups] = React.useState([]);
  const [selectedGroupId, setSelectedGroupId] = React.useState('');
  // Site-level portfolio comparison (opt-in, populated after site selection)
  const [sitePortfolioGroups, setSitePortfolioGroups] = React.useState([]);
  const [includePortfolio, setIncludePortfolio] = React.useState(false);
  const [selectedPortfolioGroupId, setSelectedPortfolioGroupId] = React.useState('');
  // Shared
  const [selectedMonth, setSelectedMonth] = React.useState(`${((new Date()).getMonth()) + 1}`);
  const [selectedYear, setSelectedYear] = React.useState(`${((new Date()).getFullYear())}`);

  const monthSelected = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  // Mutual exclusion helpers
  const hasSiteSelection = !!selectedSiteId;
  const hasPlantSelection = !!selectedPlantId;
  const hasGroupSelection = !!selectedGroupId;
  const hasAnySelection = hasSiteSelection || hasPlantSelection || hasGroupSelection;

  // -------------------------------------------------------------------------
  // Load plants and portfolio groups on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!accessToken) return;

    const headers = {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    };

    const plantsFetch = fetch(`${rootRestApiEndPoint}/reporting/plant`, { headers })
      .then(r => r.json())
      .catch(err => { console.error('Failed to load plants:', err); return []; });

    const groupsFetch = fetch(`${rootRestApiEndPoint}/portfolioGroups`, { headers })
      .then(r => r.json())
      .catch(err => { console.error('Failed to load portfolio groups:', err); return []; });

    Promise.all([plantsFetch, groupsFetch]).then(([plantsResult, groupsResult]) => {
      setPlants(plantsResult || []);
      setPortfolioGroups(groupsResult || []);
      setLoading(false);
    });
  }, [accessToken]);

  // -------------------------------------------------------------------------
  // Handlers — Plant
  // -------------------------------------------------------------------------
  const handlePlantChange = (event) => {
    setSelectedPlantId(event.target.value);
    setSelectedSiteId('');
    setSelectedGroupId('');
  };

  // -------------------------------------------------------------------------
  // Handlers — PT site search
  // -------------------------------------------------------------------------
  const handleGuessChange = (event) => setGuess(event.target.value);
  const handleGuessEuChange = (event) => setEuGuess(event.target.value);
  const handleClear = () => { setGuess(''); setSites(null); setSelectedSiteId(''); };
  const handleEuClear = () => { setEuGuess(''); setSites(null); setSelectedSiteId(''); };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') submitSiteSearch();
  };

  const handleSiteChange = (event) => {
    const site = event.target.value;
    setSelectedSiteId(event.target.value);
    setSelectedPlantId('');
    setSelectedGroupId('');
    setIncludePortfolio(false);
    setSelectedPortfolioGroupId('');
    setSitePortfolioGroups([]);

    // Fetch portfolio groups this site belongs to
    if (site?.siteId) {
      fetch(`${rootRestApiEndPoint}/portfolioGroups/site/PT/${site.siteId}`, {
        headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' }
      })
        .then(r => r.json())
        .then(groups => setSitePortfolioGroups(groups || []))
        .catch(err => console.error('Failed to load site portfolio groups:', err));
    }
  };

  const isEuGuessDisabled = guess.length > 0;
  const isGuessDisabled = euGuess.length > 0;

  const submitSiteSearch = () => {
    const isUs = guess.length > 0;
    const searchTerm = isUs ? guess : euGuess;
    if (!searchTerm) return;

    const region = isUs ? 'com' : 'eu';
    const url = `${rootRestApiEndPoint}/reporting/PT/site/${region}/${searchTerm}`;
    fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    })
      .then(r => r.json())
      .then(result => {
        setSites(result);
        setSelectedSiteId('');
      })
      .catch(err => console.error('Site search failed:', err));
  };

  // -------------------------------------------------------------------------
  // Handlers — Portfolio group
  // -------------------------------------------------------------------------
  const handleGroupChange = (event) => {
    setSelectedGroupId(event.target.value);
    setSelectedPlantId('');
    setSelectedSiteId('');
    setSites(null);
    setGuess('');
    setEuGuess('');
  };

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const submitReportRequest = (body) => {
    const url = `${rootRestApiEndPoint}/reporting/submitReportRequest`;
    fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(r => r.json())
      .then(result => {
        if (onRequestSubmitted) {
          onRequestSubmitted({
            requestType: body.portfolioGroupId && !body.siteId
              ? 'Portfolio Report'
              : 'Monthly PPTX',
            requestId: result.requestId,
          });
        }
      })
      .catch(err => console.error('Submit failed:', err));
  };

  const handleSubmit = () => {
    if (hasGroupSelection) {
      // Full portfolio report (no single site)
      submitReportRequest({
        portfolioGroupId: selectedGroupId,
        month: selectedMonth,
        year: selectedYear,
      });
    } else if (hasSiteSelection) {
      // Single PT site — optionally with portfolio comparison
      submitReportRequest({
        siteId: selectedSiteId?.siteId,
        siteName: selectedSiteId?.siteName,
        month: selectedMonth,
        year: selectedYear,
        portfolioGroupId: (includePortfolio && selectedPortfolioGroupId)
          ? selectedPortfolioGroupId
          : null,
      });
    } else if (hasPlantSelection) {
      // Legacy Plant path — still uses old endpoint
      const url = `${rootRestApiEndPoint}/reporting/submitMonthlyPptxRequest`;
      const replacer = (key, value) => (value === undefined || value === null) ? 0 : value;
      fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plantId: selectedPlantId?.PlantId,
          plantName: selectedPlantId?.PlantName,
          siteId: 0,
          siteName: '',
          month: selectedMonth,
          year: selectedYear,
        }, replacer)
      })
        .then(r => r.json())
        .catch(err => console.error('Plant submit failed:', err));
      if (onRequestSubmitted) {
        onRequestSubmitted({ requestType: 'Monthly PPTX' });
      }
    }
  };

  const isSubmitDisabled = !hasAnySelection || !selectedMonth || !selectedYear;
  const isSearchDisabled = hasPlantSelection || hasGroupSelection || (!euGuess && !guess);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  if (loading) return <CircularProgress />;

  if (!appConfig.reportsToDisplay.includes('monthlyPptxReport')) {
    return <React.Fragment />;
  }

  return (
    <Stack>
      <Card sx={{ width: '1000px' }}>
        <CardHeader title='Monthly PowerTrack IQ Report' />
        <CardContent>
          <Grid container spacing={3}>

            {/* ── Section label ── */}
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Chip label="Single Site" size="small" color={hasGroupSelection ? 'default' : 'primary'} />
                <Typography variant="body2" color="text.secondary">
                  Select a Plant or search for a PowerTrack site
                </Typography>
              </Stack>
            </Grid>

            {/* Plant selector (legacy) */}
            <Grid item xs={3}>
              <FormControl fullWidth>
                <InputLabel>Select a Plant</InputLabel>
                <Select
                  value={selectedPlantId}
                  label="Select a Plant"
                  onChange={handlePlantChange}
                  disabled={hasSiteSelection || hasGroupSelection}
                >
                  {plants.map((plant) => (
                    <MenuItem key={plant.PlantId} value={plant}>{plant.PlantName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* PT site dropdown (populated after search) */}
            <Grid item xs={3}>
              <FormControl fullWidth>
                <InputLabel>Select PT Site</InputLabel>
                <Select
                  value={selectedSiteId}
                  label="Select PT Site"
                  onChange={handleSiteChange}
                  disabled={hasPlantSelection || hasGroupSelection || !sites?.items?.length}
                >
                  {sites?.items?.map((site) => (
                    <MenuItem key={site.siteId} value={site}>{site.siteName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* US search */}
            <Grid item xs={3}>
              <TextField
                fullWidth
                value={guess}
                label="US Search String"
                onChange={handleGuessChange}
                disabled={isGuessDisabled || hasPlantSelection || hasGroupSelection}
                onKeyDown={handleKeyDown}
                InputProps={{
                  endAdornment: guess && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClear} edge="end" size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* EU search */}
            <Grid item xs={3}>
              <TextField
                fullWidth
                value={euGuess}
                label="EU Search String"
                onChange={handleGuessEuChange}
                disabled={isEuGuessDisabled || hasPlantSelection || hasGroupSelection}
                onKeyDown={handleKeyDown}
                InputProps={{
                  endAdornment: euGuess && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleEuClear} edge="end" size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {/* Search button */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={submitSiteSearch}
                disabled={isSearchDisabled}
              >
                Search
              </Button>
            </Grid>

            {/* ── Portfolio opt-in (shown only when a PT site is selected) ── */}
            {hasSiteSelection && (
              <>
                <Grid item xs={12}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Chip
                      label="Include Portfolio Comparison"
                      size="small"
                      color={includePortfolio ? 'primary' : 'default'}
                      onClick={() => {
                        setIncludePortfolio(v => !v);
                        setSelectedPortfolioGroupId('');
                      }}
                      clickable
                    />
                    <Typography variant="body2" color="text.secondary">
                      {sitePortfolioGroups.length === 0
                        ? 'This site is not a member of any portfolio group'
                        : `This site belongs to ${sitePortfolioGroups.length} portfolio group${sitePortfolioGroups.length > 1 ? 's' : ''}`}
                    </Typography>
                  </Stack>
                </Grid>

                {includePortfolio && sitePortfolioGroups.length > 0 && (
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Select Portfolio Group</InputLabel>
                      <Select
                        value={selectedPortfolioGroupId}
                        label="Select Portfolio Group"
                        onChange={e => setSelectedPortfolioGroupId(e.target.value)}
                      >
                        {sitePortfolioGroups.map(group => (
                          <MenuItem key={group.PortfolioGroupId} value={group.PortfolioGroupId}>
                            {group.GroupName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </>
            )}

            {/* ── Divider ── */}
            <Grid item xs={12}>
              <Divider>
                <Chip label="OR" size="small" />
              </Divider>
            </Grid>

            {/* ── Portfolio group section ── */}
            <Grid item xs={12}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Chip label="Portfolio Group" size="small" color={hasGroupSelection ? 'primary' : 'default'} />
                <Typography variant="body2" color="text.secondary">
                  Run a fleet report across a portfolio group
                </Typography>
              </Stack>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Select a Portfolio Group</InputLabel>
                <Select
                  value={selectedGroupId}
                  label="Select a Portfolio Group"
                  onChange={handleGroupChange}
                  disabled={hasPlantSelection || hasSiteSelection}
                >
                  {portfolioGroups.map((group) => (
                    <MenuItem key={group.PortfolioGroupId} value={group.PortfolioGroupId}>
                      {group.GroupName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* ── Divider ── */}
            <Grid item xs={12}>
              <Divider>
                <Chip label="FINALLY" size="small" />
              </Divider>
            </Grid>

            {/* ── Month selector (shared) ── */}
            <Grid item xs={8}>
              <MonthSelector
                id='downloadReportsMonthSelector'
                onSelect={monthSelected}
                prompt='Select a Month'
                month={selectedMonth}
                year={selectedYear}
              />
            </Grid>

          </Grid>
        </CardContent>

        <CardActions>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
          >
            Submit
          </Button>
        </CardActions>
      </Card>
    </Stack>
  );
};

export default DownloadReportsPanel;
